import {
  PERCEPTION_MODEL_NEW,
  PERCEPTION_MODEL_TRAIN,
  SUPERADMIN,
} from 'constants/routePaths';

import * as React from 'react';

import { useQuery } from '@apollo/client';
import {
  AllProjectNormSetsQuery,
  type PerceptionModelJobOption,
  type ProjectNormSetRow,
} from '@graphql/queries/PerceptionModelQueries';
import { Add, ArrowBack } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import selectWorkspaceProducerId from 'selectors/workspaceProducerId';

import NormSetAccordion from './NormSetAccordion';
import PerceptionModelError from './PerceptionModelError';
import PerceptionModelPicker from './PerceptionModelPicker';
import usePerceptionModels from './usePerceptionModels';
import { PageHeader, PagePaper, PageTitle } from '../../styles/themeComponents';

/**
 * Pick a model, then a norm set, then a translation.
 *
 * Model first because a norm set belongs to the one it was built on, and two
 * built on different models are not comparable. Listing them together would
 * invite exactly that comparison.
 */

/**
 * Carries the chosen model into the builder.
 *
 * Without it the builder defaults to the latest version, which quietly
 * overrides the one just picked.
 */
const newNormSetPath = (jobId?: number) =>
  jobId ? `${PERCEPTION_MODEL_NEW}?jobId=${jobId}` : PERCEPTION_MODEL_NEW;

/** Every empty state here gets one clear next action. */
const EmptyState: React.FC<{
  message: string;
  action: string;
  onAction: () => void;
}> = ({ message, action, onAction }) => (
  <Box sx={{ py: 4 }}>
    <Typography gutterBottom>{message}</Typography>
    <Button variant="contained" onClick={onAction}>
      {action}
    </Button>
  </Box>
);

const noNormSetsMessage = (elsewhere: number) =>
  `No norm sets on this model yet. Start by choosing the products your study is about.${
    elsewhere > 0
      ? ` ${elsewhere} ${elsewhere === 1 ? 'exists' : 'exist'} on another version.`
      : ''
  }`;

type StudioProps = {
  allJobs: PerceptionModelJobOption[];
  models: PerceptionModelJobOption[];
  pickerJobs: PerceptionModelJobOption[];
  job?: PerceptionModelJobOption;
  onJobId: (id: number) => void;
  normSets: ProjectNormSetRow[];
  elsewhere: number;
  onTrain: () => void;
  onNew: () => void;
};

const Studio: React.FC<StudioProps> = ({
  allJobs,
  models,
  pickerJobs,
  job,
  onJobId,
  normSets,
  elsewhere,
  onTrain,
  onNew,
}) => (
  <>
    {job && (
      <Box sx={{ mb: 2 }}>
        <PerceptionModelPicker
          jobs={pickerJobs}
          hidden={models.length > 0 ? allJobs.length - models.length : 0}
          value={job.id}
          onChange={onJobId}
        />
      </Box>
    )}

    {!allJobs.length && (
      <EmptyState
        message="No perception model has exported products yet, so there is nothing to build a norm set on."
        action="Train models"
        onAction={onTrain}
      />
    )}

    {!!allJobs.length && !models.length && (
      <Typography sx={{ color: 'text.secondary', mb: 2 }}>
        This run has not exported products yet, so a norm set cannot be built on
        it. It will show as STARTED until the container writes its row.
      </Typography>
    )}

    {!!models.length && normSets.length === 0 && (
      <EmptyState
        message={noNormSetsMessage(elsewhere)}
        action="New norm set"
        onAction={onNew}
      />
    )}

    {normSets.length > 0 && (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0.5,
        }}
      >
        <Typography variant="subtitle2">
          {normSets.length} {normSets.length === 1 ? 'norm set' : 'norm sets'}{' '}
          on this model
        </Typography>
        <Button
          size="small"
          startIcon={<Add fontSize="small" />}
          onClick={onNew}
          sx={{ textTransform: 'none' }}
        >
          New norm set
        </Button>
      </Box>
    )}

    {normSets.map((ns) => (
      <NormSetAccordion key={ns.id} normSet={ns} />
    ))}
  </>
);

const NormSetList: React.FC = () => {
  const history = useHistory();
  const producerId = useSelector((state) => selectWorkspaceProducerId(state));

  const {
    allJobs,
    models,
    loading: modelsLoading,
    error: modelsError,
  } = usePerceptionModels();
  const [jobId, setJobId] = React.useState<number | undefined>();
  // A just-started run has no lookup rows yet. If we only offered exported
  // models, the landing page would claim nothing exists the moment training
  // is kicked off. Fall back to the in-flight jobs so STARTED is visible.
  const pickerJobs = models.length > 0 ? models : allJobs;
  const job = pickerJobs.find((j) => j.id === jobId) ?? pickerJobs[0];

  const { data, loading, error } = useQuery(AllProjectNormSetsQuery, {
    variables: { producerId },
    skip: !producerId,
  });
  // A skipped query does not report as loading, so without folding the missing
  // producer in here the page would claim "no norm sets yet" for a moment
  // before the workspace resolves — an empty studio, not a slow one.
  const pending = loading || !producerId || modelsLoading;

  const allNormSets: ProjectNormSetRow[] =
    data?.allPerceptionModelProjects?.nodes ?? [];
  const normSets = job
    ? allNormSets.filter((ns) => ns.perceptionModelJobId === job.id)
    : allNormSets;
  const elsewhere = allNormSets.length - normSets.length;

  const schemaError = error || modelsError;
  const ready = !pending && !schemaError;

  return (
    <PagePaper>
      {/* The other Superadmin Tools entry point, and equally not in the
          sidebar, so it needs the same way back as the train page. */}
      <Button
        size="small"
        startIcon={<ArrowBack fontSize="small" />}
        onClick={() => history.push(SUPERADMIN)}
        sx={{ textTransform: 'none', mb: 1 }}
      >
        Superadmin Tools
      </Button>

      <PageHeader>Perception models</PageHeader>
      <PageTitle>Norm sets</PageTitle>

      {pending && <Typography>Loading…</Typography>}

      {schemaError && <PerceptionModelError error={schemaError} />}

      {ready && (
        <Studio
          allJobs={allJobs}
          models={models}
          pickerJobs={pickerJobs}
          job={job}
          onJobId={setJobId}
          normSets={normSets}
          elsewhere={elsewhere}
          onTrain={() => history.push(PERCEPTION_MODEL_TRAIN)}
          onNew={() => history.push(newNormSetPath(job?.id))}
        />
      )}
    </PagePaper>
  );
};

export default NormSetList;
